import { useMemo, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Loader2, BarChart3 } from "lucide-react";

import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";
import {
    getUnitWiseCostDoughnutData,
    getUnitWiseCostDoughnutOptions,
    unitWiseCostCenterTextPlugin,
    generateUnitColors,
} from "./chartConfigs/unitWiseCostDoughnutChart";
import {
    COST_TYPE_OPTIONS,
    // VALUE_MODE_OPTIONS - kept for the commented-out Value Mode filter below.
    VALUE_MODE_OPTIONS,
    formatUnitValue,
    getChartFilterSummary,
} from "./unitWiseCostFilterOptions";
import { useUnitWiseCostData, makeProjectKey } from "./useUnitWiseCostData";
import ChartFilterBadges from "./ChartFilterBadges";

/**
 * Fully independent doughnut chart widget: owns its own FY / cost-type /
 * project filters and fetches its own data, independent of
 * UnitWiseCostBarChart or any other instance. Both the Percentage view and
 * the Crore view are always fetched and rendered together (Percentage on
 * top, Crore below) so a change to FY, Cost Type or Projects updates both
 * sections in lockstep.
 *
 * @param {object[]} masterProjects - shared project list (fetched once by parent)
 */
const UnitWiseCostDoughnutChart = ({ masterProjects = [] }) => {

    const [selectedFY, setSelectedFY] = useState("OVERALL");
    const [costLevel, setCostLevel] = useState("DIRECT_MATERIAL");
    const [valueMode, setValueMode] = useState("CRORE");
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);

    const projectKeyToProject = useMemo(() => {

        const map = new Map();

        masterProjects.forEach((project) => {
            map.set(makeProjectKey(project.IO_NO, project.REV_NO), project);
        });

        return map;

    }, [masterProjects]);

    // Both modes are fetched unconditionally, with the same FY/Project
    // filters, so changing either filter refreshes both sections.
    const percentResult = useUnitWiseCostData({
        costLevel,
        valueMode: "PERCENT",
        selectedFY,
        selectedProjectKeys,
        projectKeyToProject,
    });

    const croreResult = useUnitWiseCostData({
        costLevel,
        valueMode: "CRORE",
        selectedFY,
        selectedProjectKeys,
        projectKeyToProject,
    });

    const loading = percentResult.loading || croreResult.loading;
    const financialYearOptions = percentResult.financialYearOptions;

    /* ---------------------------------------------------------------- */
    /*                          PERCENTAGE SECTION                       */
    /* ---------------------------------------------------------------- */

    const percentDoughnutData = useMemo(
        () => getUnitWiseCostDoughnutData(percentResult.sortedUnitData, percentResult.selectedOverallValue, "Overall"),
        [percentResult.sortedUnitData, percentResult.selectedOverallValue]
    );

    const percentDoughnutOptions = useMemo(
        () => getUnitWiseCostDoughnutOptions("PERCENT"),
        []
    );

    const percentColors = useMemo(
        () => generateUnitColors(percentResult.sortedUnitData.length),
        [percentResult.sortedUnitData.length]
    );

    /* ---------------------------------------------------------------- */
    /*                             CRORE SECTION                         */
    /* ---------------------------------------------------------------- */

    const croreDoughnutData = useMemo(
        () => getUnitWiseCostDoughnutData(croreResult.sortedUnitData, croreResult.selectedOverallValue, "Overall"),
        [croreResult.sortedUnitData, croreResult.selectedOverallValue]
    );

    const croreDoughnutOptions = useMemo(
        () => getUnitWiseCostDoughnutOptions("CRORE"),
        []
    );

    const croreColors = useMemo(
        () => generateUnitColors(croreResult.sortedUnitData.length),
        [croreResult.sortedUnitData.length]
    );

    // Composition chart (unit's crore value / total crore value * 100),
    // shown alongside the crore amount chart.
    const compositionUnitData = useMemo(
        () => croreResult.sortedUnitData.map((unit) => ({
            ...unit,
            value: croreResult.selectedOverallValue > 0
                ? (unit.value / croreResult.selectedOverallValue) * 100
                : 0,
        })),
        [croreResult.sortedUnitData, croreResult.selectedOverallValue]
    );

    const compositionDoughnutData = useMemo(
        () => getUnitWiseCostDoughnutData(compositionUnitData, 100, "Composition"),
        [compositionUnitData]
    );

    const compositionDoughnutOptions = useMemo(
        () => getUnitWiseCostDoughnutOptions("PERCENT"),
        []
    );

    /* ---------------------------------------------------------------- */
    /*                      CARD <-> DOUGHNUT HOVER SYNC                 */
    /* ---------------------------------------------------------------- */

    // Lets a Unit card drive the tooltip/hover highlight on its section's
    // doughnut(s), in addition to the doughnut's own native mouse hover.
    const percentChartRef = useRef(null);
    const amountChartRef = useRef(null);
    const compositionChartRef = useRef(null);

    const setChartHighlight = (chartRef, index) => {

        const chart = chartRef.current;

        if (!chart) return;

        const activeElements = index === null
            ? []
            : [{ datasetIndex: 0, index }];

        chart.setActiveElements(activeElements);
        chart.tooltip?.setActiveElements(activeElements, { x: 0, y: 0 });
        chart.update();

    };

    const handlePercentCardMouseEnter = (index) => setChartHighlight(percentChartRef, index);
    const handlePercentCardMouseLeave = () => setChartHighlight(percentChartRef, null);

    const handleCroreCardMouseEnter = (index) => {
        setChartHighlight(amountChartRef, index);
        setChartHighlight(compositionChartRef, index);
    };

    const handleCroreCardMouseLeave = () => {
        setChartHighlight(amountChartRef, null);
        setChartHighlight(compositionChartRef, null);
    };

    const filterSummary = useMemo(
        () => getChartFilterSummary({
            costLevel,
            selectedFY,
            valueMode,
            selectedProjectKeys,
            projectKeyToProject,
        }),
        [costLevel, selectedFY, valueMode, selectedProjectKeys, projectKeyToProject]
    );

    return (

        <div className="bg-white border border-[#EAECF0] rounded-xl p-5">

            {/* FILTERS */}

            <div
                className="
                    flex
                    flex-wrap
                    items-start
                    gap-4
                    p-4
                    mb-6
                    bg-[#F9FAFB]
                    border
                    border-[#EAECF0]
                    rounded-xl
                "
            >

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Financial Year</span>
                    <CustomSelectWithFilter
                        data={financialYearOptions}
                        value={selectedFY}
                        onChange={(value) => setSelectedFY(value || "OVERALL")}
                        placeholder="Overall"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Cost Type</span>
                    <CustomSelectWithFilter
                        data={COST_TYPE_OPTIONS}
                        value={costLevel}
                        onChange={setCostLevel}
                        placeholder="Cost Type"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>

                {/* Value Mode filter disabled - Percentage and Crore views
                    now always render together, so this selector no longer
                    switches anything. Left here (commented) in case it
                    needs to come back.
                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Value Mode</span>
                    <CustomSelectWithFilter
                        data={VALUE_MODE_OPTIONS}
                        value={valueMode}
                        onChange={setValueMode}
                        placeholder="Value Mode"
                        className="w-[200px]"
                        allowClear={false}
                    />
                </div>
                */}

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-[#667085]">Projects Filter</span>
                    <CustomMultiSelect
                        data={masterProjects.map((project) => ({
                            label: project.PROJ_NAME,
                            value: makeProjectKey(project.IO_NO, project.REV_NO),
                        }))}
                        value={selectedProjectKeys}
                        onChange={setSelectedProjectKeys}
                        placeholder="All Projects"
                        className="w-[260px]"
                    />
                </div>

            </div>

            {/* HEADER */}

            <div className="flex items-center gap-2 mb-6">
                <BarChart3 size={20} className="text-[#155EEF]" />
                <div>
                    <div className="text-xl font-semibold text-[#101828]">
                        Unit Wise {costLevel === "TOTAL_COST"
                            ? "Factory Cost Level"
                            : `${filterSummary.costTypeLabel} Cost`}
                    </div>
                    <ChartFilterBadges
                        fyLabel={filterSummary.fyLabel}
                        valueMode={valueMode}
                        valueModeLabel="Percentage & Crore (₹)"
                        projectNames={filterSummary.projectNames}
                    />
                </div>
            </div>

            {/* CONTENT */}

            {loading ? (
                <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-[#155EEF]" />
                </div>
            ) : (
                <div className="flex flex-col gap-10 w-full">

                    {/* ============================================= */}
                    {/*                PERCENTAGE SECTION               */}
                    {/* ============================================= */}

                    <div className="flex flex-col w-full">

                        <div className="text-sm font-semibold text-[#101828] mb-4">
                            Percentage (%)
                        </div>

                        {percentResult.sortedUnitData.length === 0 ? (
                            <div className="flex items-center justify-center h-[200px] text-sm text-[#667085]">
                                No data available
                            </div>
                        ) : (
                            <div className="flex flex-col xl:flex-row gap-8 w-full">

                                {/* LEFT - DOUGHNUT */}

                                <div className="w-full xl:w-1/2 flex items-center justify-center min-h-[340px]">
                                    <div className="relative w-full max-w-[380px] h-[340px]">
                                        <Doughnut
                                            ref={percentChartRef}
                                            data={percentDoughnutData}
                                            options={percentDoughnutOptions}
                                            plugins={[unitWiseCostCenterTextPlugin("PERCENT")]}
                                        />
                                    </div>
                                </div>

                                {/* RIGHT - CUSTOM LEGEND */}

                                <div className="w-full xl:w-1/2 flex flex-col">

                                    <div
                                        className="
                                            grid
                                            grid-cols-1
                                            2xl:grid-cols-2
                                            gap-3
                                            max-h-[380px]
                                            overflow-y-auto
                                            pr-2
                                        "
                                    >
                                        {percentResult.sortedUnitData.map((unit, index) => {

                                            const color = percentColors[index];

                                            return (
                                                <div
                                                    key={unit.unitCode}
                                                    onMouseEnter={() => handlePercentCardMouseEnter(index)}
                                                    onMouseLeave={handlePercentCardMouseLeave}
                                                    className="
                                                        relative
                                                        flex
                                                        items-center
                                                        justify-between
                                                        gap-4
                                                        min-h-[52px]
                                                        px-4
                                                        py-3
                                                        bg-white
                                                        border
                                                        border-[#EAECF0]
                                                        rounded-lg
                                                        overflow-hidden
                                                        cursor-pointer
                                                        transition-colors
                                                        hover:border-[#155EEF]
                                                        hover:bg-[#F5F8FF]
                                                    "
                                                >
                                                    <div
                                                        className="absolute left-0 top-0 bottom-0 w-[3px]"
                                                        style={{ backgroundColor: color }}
                                                    />

                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span
                                                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                            style={{ backgroundColor: color }}
                                                        />
                                                        <span
                                                            className="text-xs font-medium text-[#344054] truncate"
                                                            title={unit.unitName}
                                                        >
                                                            {unit.unitName}
                                                        </span>
                                                    </div>

                                                    <span className="text-xs font-semibold text-[#101828] whitespace-nowrap">
                                                        {formatUnitValue(unit.value, "PERCENT")}
                                                    </span>
                                                </div>
                                            );

                                        })}
                                    </div>

                                    <div className="flex justify-end mt-4 pt-3 border-t border-[#EAECF0]">
                                        <span className="text-sm text-[#667085]">
                                            Total Units:{" "}
                                            <span className="font-semibold text-[#101828]">
                                                {percentResult.sortedUnitData.length}
                                            </span>
                                        </span>
                                    </div>

                                </div>

                            </div>
                        )}

                    </div>

                    <div className="border-t border-[#EAECF0]" />

                    {/* ============================================= */}
                    {/*                   CRORE SECTION                 */}
                    {/* ============================================= */}

                    <div className="flex flex-col w-full">

                        <div className="text-sm font-semibold text-[#101828] mb-4">
                            Crore (₹)
                        </div>

                        {croreResult.sortedUnitData.length === 0 ? (
                            <div className="flex items-center justify-center h-[200px] text-sm text-[#667085]">
                                No data available
                            </div>
                        ) : (
                            <>
                                {/* TOP - TWO DOUGHNUTS SIDE BY SIDE */}

                                <div className="flex flex-col md:flex-row gap-8 w-full">

                                    <div className="w-full md:w-1/2 flex flex-col items-center">
                                        <span className="text-sm font-medium text-[#344054] mb-2">
                                            Cost (₹ Cr)
                                        </span>
                                        <div className="relative w-full max-w-[380px] h-[340px]">
                                            <Doughnut
                                                ref={amountChartRef}
                                                data={croreDoughnutData}
                                                options={croreDoughnutOptions}
                                                plugins={[unitWiseCostCenterTextPlugin("CRORE")]}
                                            />
                                        </div>
                                    </div>

                                    <div className="w-full md:w-1/2 flex flex-col items-center">
                                        <span className="text-sm font-medium text-[#344054] mb-2">
                                            Composition (%)
                                        </span>
                                        <div className="relative w-full max-w-[380px] h-[340px]">
                                            <Doughnut
                                                ref={compositionChartRef}
                                                data={compositionDoughnutData}
                                                options={compositionDoughnutOptions}
                                                plugins={[unitWiseCostCenterTextPlugin("PERCENT")]}
                                            />
                                        </div>
                                    </div>

                                </div>

                                {/* BOTTOM - UNIT CARDS */}

                                <div
                                    className="
                                        grid
                                        grid-cols-1
                                        sm:grid-cols-2
                                        lg:grid-cols-3
                                        xl:grid-cols-4
                                        gap-3
                                        mt-8
                                    "
                                >
                                    {croreResult.sortedUnitData.map((unit, index) => {

                                        const color = croreColors[index];
                                        const composition = compositionUnitData[index]?.value ?? 0;

                                        return (
                                            <div
                                                key={unit.unitCode}
                                                onMouseEnter={() => handleCroreCardMouseEnter(index)}
                                                onMouseLeave={handleCroreCardMouseLeave}
                                                className="
                                                    relative
                                                    flex
                                                    items-center
                                                    justify-between
                                                    gap-4
                                                    min-h-[52px]
                                                    px-4
                                                    py-3
                                                    bg-white
                                                    border
                                                    border-[#EAECF0]
                                                    rounded-lg
                                                    overflow-hidden
                                                    cursor-pointer
                                                    transition-colors
                                                    hover:border-[#155EEF]
                                                    hover:bg-[#F5F8FF]
                                                "
                                            >
                                                <div
                                                    className="absolute left-0 top-0 bottom-0 w-[3px]"
                                                    style={{ backgroundColor: color }}
                                                />

                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span
                                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: color }}
                                                    />
                                                    <span
                                                        className="text-xs font-medium text-[#344054] truncate"
                                                        title={unit.unitName}
                                                    >
                                                        {unit.unitName} ({composition.toFixed(2)}%)
                                                    </span>
                                                </div>

                                                <span className="text-xs font-semibold text-[#101828] whitespace-nowrap">
                                                    {formatUnitValue(unit.value, "CRORE")}
                                                </span>
                                            </div>
                                        );

                                    })}
                                </div>

                                <div className="flex justify-end mt-4 pt-3 border-t border-[#EAECF0]">
                                    <span className="text-sm text-[#667085]">
                                        Total Units:{" "}
                                        <span className="font-semibold text-[#101828]">
                                            {croreResult.sortedUnitData.length}
                                        </span>
                                    </span>
                                </div>
                            </>
                        )}

                    </div>

                </div>
            )}

        </div>

    );

};

export default UnitWiseCostDoughnutChart;
